import { FC } from "react";
import { CalendarX2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import Throbber from "@/components/throbber";
import { useReactivateCardSubscription } from "@/hooks/use-mutations";
import { useCardStyle } from "@/hooks/use-card-style";

export const SubscriptionCancelledBanner: FC<{ card: Card }> = ({ card }) => {
  const getCardStyle = useCardStyle();
  const tier = getCardStyle(card.bin, card.network, card.provider).tierLabel;
  return (
    <div className="mb-4 flex items-center gap-3 rounded-2xl border border-[#F87171]/60 bg-[#F87171]/10 p-3 text-white">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[#F87171]/20">
        <CalendarX2 className="size-5 text-[#F87171]" strokeWidth={1.5} />
      </div>
      <div>
        <p className="text-sm font-semibold">Subscription Cancelled</p>
        <p className="text-[11px] text-white/70">
          Your {tier ? `${tier} ` : ""}plan has been cancelled. Reactivate your
          subscription to continue using your card.
        </p>
      </div>
    </div>
  );
};

export const ReactivateSubscriptionPanel: FC<{ card: Card }> = ({ card }) => {
  const queryClient = useQueryClient();
  const { mutate: reactivate, isPending } = useReactivateCardSubscription({
    id: card.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cardInfo", card.id] });
      queryClient.invalidateQueries({
        queryKey: ["cardSubscription", card.id],
      });
      queryClient.invalidateQueries({ queryKey: ["cards"] });
    },
  });

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-[#EBE8F308] p-4 text-white">
      <p className="text-base font-medium">Reactivate subscription</p>
      <p className="mt-1 text-xs text-white/60">
        Enable your subscription to keep your card and continue enjoying all
        benefits.
      </p>
      <button
        type="button"
        onClick={() => reactivate()}
        disabled={isPending}
        className="mt-4 flex h-11 w-full items-center justify-center rounded-lg bg-[#FFF8EC] text-sm font-medium text-[#242424] hover:bg-[#FFF8EC]/90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isPending ? <Throbber /> : "Enable subscription"}
      </button>
    </div>
  );
};
